/**
 * Home: the DM list beside, and the Friends page (prototype dmSidebar,
 * friendsMain); a DM's header actions. Relationships and DMs come from
 * READY and RELATIONSHIP_*. Layout: visual-overhaul.md §11 WP6 (one
 * column of 56px friend rows, a 36px search field; Active Now deferred).
 */
import {
  ChannelType,
  RelationshipType,
  type Channel,
  type Relationship,
  type Snowflake,
} from '@neosocial/protocol';
import { useLayout } from '@neosocial/ui/layout';
import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { useT } from '../i18n';
import { useUm } from '../store/um';
import { useData } from '../store/data';
import { useDataShallow } from '../store/data';
import {
  displayName,
  presenceOf,
  privateChannelName,
  privateChannels,
  unreadOf,
} from '../store/selectors';
import { noteMenuItems, useStatusLine } from '../social/ProfileSocial';
import { goHub, useUi } from '../store/ui';
import { Avatar, Btn, EmptyState, gradStyle, Icon, IconBtn } from '../ui/kit';
import { openMenu } from '../ui/overlay';
import { DmDialogs, dmMenuItems, GroupHeaderActions, matchesPerson, openDmDialog } from './GroupDm';
import { CallButton } from './DmCall';
import { openProfile } from './ProfileCard';
import {
  acceptFriendWithToast,
  closeDm,
  confirmBlock,
  confirmRemoveFriend,
  leaveGroup,
  openDm,
  removeRelationship,
  sendFriendRequest,
  unblock,
  useIsIgnored,
} from './social';

export function DmSidebar() {
  const { t, fmt } = useT();
  const view = useUi((u) => u.hub);
  const dms = useDataShallow(privateChannels);
  const pending = useData(
    (d) =>
      Object.values(d.relationships).filter((r) => r.type === RelationshipType.PENDING_INCOMING)
        .length,
  );
  const openId = view?.kind === 'home' ? view.channelId : undefined;
  const onRequests = view?.kind === 'home' && view.page === 'requests';
  const requestCount = useUm((s) => s.requestCount);
  return (
    <div className="hubv-dms">
      <div className="hubv-dms-scroll nx-scroll">
        <nav className="hubv-dms-nav" aria-label={t('hub.home')}>
          <button
            type="button"
            className="hubv-dmn"
            aria-current={!openId && !onRequests ? 'page' : undefined}
            onClick={() => goHub({ kind: 'home' })}
          >
            <Icon name="users" />
            <span>{t('hubv.dm.friends')}</span>
            {pending ? <b className="nx-badge">{fmt.num(pending)}</b> : null}
          </button>
          <button
            type="button"
            className="hubv-dmn"
            aria-current={onRequests ? 'page' : undefined}
            onClick={() => goHub({ kind: 'home', page: 'requests' })}
          >
            <Icon name="inbox" />
            <span>{t('requests.title')}</span>
            {requestCount ? <b className="nx-badge">{fmt.num(requestCount)}</b> : null}
          </button>
        </nav>
        <div className="hubv-dms-h">
          <span>{t('hubv.dm.title')}</span>
          <button
            type="button"
            className="hubv-dms-plus"
            aria-label={t('hubv.dm.create')}
            data-tip={t('hubv.dm.create')}
            onClick={() => openDmDialog({ kind: 'new' })}
          >
            <Icon name="plus" />
          </button>
        </div>
        <ul className="hubv-dml" role="list">
          {dms.map((c) => (
            <DmRow key={c.id} id={c.id} sel={c.id === openId} />
          ))}
        </ul>
        {!dms.length ? <p className="ns-empty-side">{t('ns.dm.none')}</p> : null}
        <div className="hub-side-pad" aria-hidden="true" />
      </div>
      <DmDialogs />
    </div>
  );
}

function DmRow({ id, sel }: { id: Snowflake; sel: boolean }) {
  const { t, fmt } = useT();
  const c = useData((d) => d.channels[id]);
  const name = useData((d) => (c ? privateChannelName(d, c) : ''));
  const peer = useData((d) =>
    c?.type === ChannelType.DM ? c.recipients?.find((u) => u.id !== d.me?.id) : undefined,
  );
  const p = useData((d) => (peer ? presenceOf(d, peer.id) : null));
  // the custom status, else "Playing X" (rich presence, 2026-10-29)
  const custom = useStatusLine(peer?.id);
  const st = useDataShallow((d) => unreadOf(d, id));
  // §3.2: an ignored person's DM is greyed out and silent
  const ignored = useIsIgnored(peer?.id);
  if (!c) return null;
  const group = c.type === ChannelType.GROUP_DM;
  const unread = st.unread && !ignored;
  const closeLabel = group ? t('gdm.leave') : t('dm.close');
  return (
    <li>
      <button
        type="button"
        className={'hubv-dmr' + (unread ? ' unread' : '') + (ignored ? ' ns-dm-ignored' : '')}
        aria-current={sel ? 'page' : undefined}
        aria-label={name + (unread ? ', ' + t('hub.ch.unread') : '')}
        onClick={() => goHub({ kind: 'home', channelId: id })}
        onContextMenu={(e) => {
          e.preventDefault();
          const el = e.currentTarget;
          // Discord's "Add Note" on a DM (2026-10-29): their profile opens with the note ready
          const note = peer ? noteMenuItems(peer.id, () => openProfile(peer.id, el)) : [];
          openMenu({ x: e.clientX, y: e.clientY }, [...dmMenuItems(c), ...note], { label: name });
        }}
      >
        {group ? (
          <span
            className="nx-av ns-group-av"
            style={{ '--s': '32px', ...gradStyle(c.id) } as CSSProperties}
            aria-hidden="true"
          >
            <span className="nx-av-in">
              <Icon name="users" />
            </span>
          </span>
        ) : (
          <span className="hubv-dmr-av">
            <Avatar user={peer} size={32} presence={p} />
          </span>
        )}
        <span className="t">
          <b dir="auto">{name}</b>
          {group ? (
            <small>{t('hubv.dm.members', { n: (c.recipients?.length ?? 0) + 1 })}</small>
          ) : p ? (
            <small dir="auto">{custom ?? t(`pres.${p}`)}</small>
          ) : null}
        </span>
        {st.mentions && !ignored ? (
          <b className="nx-badge hubv-dmr-badge">{fmt.num(st.mentions)}</b>
        ) : null}
      </button>
      <button
        type="button"
        className="hubv-dmx"
        aria-label={`${closeLabel}: ${name}`}
        data-tip={closeLabel}
        onClick={() => void (group ? leaveGroup(id, name) : closeDm(id))}
      >
        <Icon name="x" />
      </button>
    </li>
  );
}

type Tab = 'online' | 'all' | 'pending' | 'blocked' | 'add';

export function FriendsPage() {
  const { t, fmt } = useT();
  const [tab, setTab] = useState<Tab>('online');
  const rels = useData((d) => d.relationships);
  const pend = Object.values(rels).filter(
    (r) => r.type === RelationshipType.PENDING_INCOMING,
  ).length;
  const tabs: Array<[Tab, string]> = [
    ['online', 'hubv.fr.online'],
    ['all', 'hubv.fr.all'],
    ['pending', 'hubv.fr.pending'],
    ['blocked', 'hubv.fr.blocked'],
  ];
  // a phone (§12.7 B5): the tabs are a row of their own under the header,
  // one line that scrolls sideways; the header keeps Back and the title
  const phone = useLayout().layout === 'phone';
  const tabRow = (
    <div className="hubv-fr-tabs" role="tablist">
      {tabs.map(([k, label]) => (
        <button
          key={k}
          type="button"
          role="tab"
          className="hubv-frt"
          data-tab={k}
          aria-selected={tab === k}
          onClick={() => setTab(k)}
        >
          {t(label)}
          {k === 'pending' && pend ? <b className="nx-badge">{fmt.num(pend)}</b> : null}
        </button>
      ))}
      <button
        type="button"
        role="tab"
        className="hubv-frt add"
        data-tab="add"
        aria-selected={tab === 'add'}
        onClick={() => setTab('add')}
      >
        {t('hubv.fr.add')}
      </button>
    </div>
  );
  return (
    <section className="hub-pane nx-panel custom hubv-dm" aria-label={t('hubv.dm.friends')}>
      <header className="ch-head hubv-fr-head">
        <div className="ch-title">
          <span className="ch-title-i" aria-hidden="true">
            <Icon name="users" />
          </span>
          <h1>{t('hubv.dm.friends')}</h1>
          {phone ? null : (
            <>
              <span className="ch-sep" aria-hidden="true" />
              {tabRow}
            </>
          )}
        </div>
      </header>
      {phone ? <div className="hubv-fr-tabbar">{tabRow}</div> : null}
      <div className="hubv-fr">
        <div className="hubv-fr-main nx-scroll">
          {tab === 'add' ? <AddFriend /> : <FriendRows tab={tab} onAdd={() => setTab('add')} />}
        </div>
      </div>
    </section>
  );
}

function AddFriend() {
  const { t } = useT();
  const [v, setV] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'ok' | 'bad'>('idle');
  const [sentTo, setSentTo] = useState('');
  return (
    <div className="hubv-fr-add nx-arrive">
      <h2>{t('hubv.fr.addTitle')}</h2>
      <p>{t('ns.fr.addSub')}</p>
      <form
        className={'hubv-fr-addf' + (state === 'ok' ? ' ok' : state === 'bad' ? ' bad' : '')}
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!v.trim()) return;
          setState('busy');
          const r = await sendFriendRequest(v);
          if (r === 'sent') {
            setSentTo(v.trim().replace(/^@/, ''));
            setV('');
            setState('ok');
          } else setState('bad');
        }}
      >
        <input
          className="hubv-fr-addin"
          dir="ltr"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('ns.fr.addPh')}
          aria-label={t('ns.fr.addPh')}
          aria-invalid={state === 'bad' || undefined}
          value={v}
          data-autofocus
          onChange={(e) => {
            setV(e.target.value);
            if (state !== 'busy') setState('idle');
          }}
        />
        <Btn
          kind="primary"
          size="xs"
          type="submit"
          disabled={!v.trim() || state === 'busy'}
          busy={state === 'busy'}
        >
          {t('hubv.fr.send')}
        </Btn>
      </form>
      {state === 'ok' ? (
        <p className="hubv-fr-addmsg ok" role="status">
          {t('hubv.fr.sent', { name: sentTo })}
        </p>
      ) : null}
    </div>
  );
}

/** An empty tab says what goes there and, where there is one, the next step (I30). */
function emptyCopy(
  t: ReturnType<typeof useT>['t'],
  tab: Exclude<Tab, 'add'>,
  q: string,
): { title: string; hint: string } {
  if (q) return { title: t('hubv.fr.none', { q }), hint: t('people.fr.searchHint') };
  switch (tab) {
    case 'online':
      return { title: t('people.fr.onlineNone'), hint: t('people.fr.onlineHint') };
    case 'all':
      return { title: t('people.fr.allNone'), hint: t('people.fr.allHint') };
    case 'pending':
      return { title: t('hubv.fr.pendingNone'), hint: t('people.fr.pendingHint') };
    default:
      return { title: t('hubv.fr.blockedNone'), hint: t('people.fr.blockedHint') };
  }
}

function FriendRows({ tab, onAdd }: { tab: Exclude<Tab, 'add'>; onAdd(): void }) {
  const { t } = useT();
  const [q, setQ] = useState('');
  const rels = useData((d) => d.relationships);
  const presences = useData((d) => d.presences);
  const list = useMemo(() => {
    const d = { ...useData.getState(), presences };
    let l: Relationship[] = Object.values(rels);
    if (tab === 'online')
      l = l.filter((r) => r.type === RelationshipType.FRIEND && presenceOf(d, r.id) !== 'offline');
    else if (tab === 'all') l = l.filter((r) => r.type === RelationshipType.FRIEND);
    else if (tab === 'pending')
      l = l.filter(
        (r) =>
          r.type === RelationshipType.PENDING_INCOMING ||
          r.type === RelationshipType.PENDING_OUTGOING,
      );
    else l = l.filter((r) => r.type === RelationshipType.BLOCKED);
    // the same matching as Ctrl+K: Arabic-script letters and digits normalised, "@" ignored
    if (q.trim()) l = l.filter((r) => matchesPerson(d.users[r.id] ?? r.user, q));
    return l.sort((a, b) => displayName(a.user).localeCompare(displayName(b.user)));
    // presences decide who is online
  }, [rels, tab, q, presences]);
  const label = t(`hubv.fr.${tab}`);
  const empty = emptyCopy(t, tab, q.trim());
  return (
    <>
      <label className="hubv-fr-search">
        <Icon name="search" />
        <input
          type="search"
          dir="auto"
          placeholder={t('hubv.fr.search')}
          aria-label={t('hubv.fr.search')}
          value={q}
          autoComplete="off"
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      <div className="hubv-fr-rows">
        {!list.length ? (
          <EmptyState
            icon={q.trim() ? 'search' : tab === 'blocked' ? 'shield' : 'users'}
            title={empty.title}
            hint={empty.hint}
            action={
              !q.trim() && (tab === 'online' || tab === 'all') ? (
                <Btn kind="ghost" size="sm" icon="user-plus" onClick={onAdd}>
                  {t('people.fr.addCta')}
                </Btn>
              ) : undefined
            }
          />
        ) : (
          <>
            <h3 className="hubv-fr-gh">{t('hubv.fr.group', { label, n: list.length })}</h3>
            {list.map((r) => (
              <FriendRow key={r.id} r={r} />
            ))}
          </>
        )}
      </div>
    </>
  );
}

function FriendRow({ r }: { r: Relationship }) {
  const { t } = useT();
  const user = useData((d) => d.users[r.id] ?? r.user);
  const p = useData((d) => presenceOf(d, r.id));
  const custom = useStatusLine(r.id);
  const name = displayName(user);
  const incoming = r.type === RelationshipType.PENDING_INCOMING;
  const outgoing = r.type === RelationshipType.PENDING_OUTGOING;
  const blocked = r.type === RelationshipType.BLOCKED;
  const sub = incoming
    ? t('hubv.fr.incoming')
    : outgoing
      ? t('ns.fr.outgoing')
      : blocked
        ? t('ns.fr.blockedSub')
        : (custom ?? t(`pres.${p}`));
  return (
    <div className="hubv-frr">
      <button
        type="button"
        className="hubv-frr-id"
        onClick={(e) => openProfile(r.id, e.currentTarget)}
      >
        <Avatar user={user} size={32} presence={blocked ? null : p} />
        <span className="t">
          <span className="n">
            <b dir="auto">{name}</b>
            <span className="un" dir="ltr">
              {user.username}
            </span>
          </span>
          <small dir="auto">
            {r.nx_request_note && incoming ? `${sub} · ${r.nx_request_note}` : sub}
          </small>
        </span>
      </button>
      <div className="hubv-frr-acts">
        {incoming ? (
          <>
            <button
              type="button"
              className="hubv-fra ok"
              aria-label={t('hubv.fr.accept')}
              data-tip={t('hubv.fr.accept')}
              onClick={() => void acceptFriendWithToast(r.id, name)}
            >
              <Icon name="check" />
            </button>
            <button
              type="button"
              className="hubv-fra no"
              aria-label={t('hubv.fr.ignore')}
              data-tip={t('hubv.fr.ignore')}
              onClick={() => void removeRelationship(r.id)}
            >
              <Icon name="x" />
            </button>
          </>
        ) : outgoing || blocked ? (
          <button
            type="button"
            className="hubv-fra no"
            aria-label={blocked ? t('ns.fr.unblock') : t('ns.fr.cancel')}
            data-tip={blocked ? t('ns.fr.unblock') : t('ns.fr.cancel')}
            onClick={() => void (blocked ? unblock(r.id, name) : removeRelationship(r.id))}
          >
            <Icon name="x" />
          </button>
        ) : (
          <>
            <button
              type="button"
              className="hubv-fra"
              aria-label={t('hubv.fr.message')}
              data-tip={t('hubv.fr.message')}
              onClick={() => void openDm(r.id)}
            >
              <Icon name="chats" />
            </button>
            <button
              type="button"
              className="hubv-fra"
              aria-haspopup="menu"
              aria-label={t('hubv.fr.more')}
              data-tip={t('hubv.fr.more')}
              onClick={(e) =>
                openMenu(
                  e.currentTarget,
                  [
                    { label: t('pres.profile'), icon: 'user', run: () => openProfile(r.id, null) },
                    { sep: true },
                    {
                      label: t('hubv.fr.remove'),
                      icon: 'x',
                      danger: true,
                      run: () => void confirmRemoveFriend(r.id, name),
                    },
                    {
                      label: t('ns.fr.block'),
                      icon: 'shield',
                      danger: true,
                      run: () => void confirmBlock(r.id, name),
                    },
                  ],
                  { placement: 'bottom-end' },
                )
              }
            >
              <Icon name="more-v" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A DM's header actions, only those that exist (§11 WP6): a group's Add,
 * Members and More; a DM's View profile (its card under the button).
 */
export function DmHeaderActions({ channel, compact }: { channel: Channel; compact?: boolean }) {
  const { t } = useT();
  const peer = useData((d) =>
    channel.type === ChannelType.DM
      ? channel.recipients?.find((u) => u.id !== d.me?.id)
      : undefined,
  );
  // the Call button first, as Discord's DM header has it (DM calls, 2026-11-01)
  if (channel.type === ChannelType.GROUP_DM)
    return (
      <>
        <CallButton channel={channel} />
        <GroupHeaderActions channel={channel} compact={compact} />
      </>
    );
  if (!peer) return null;
  return (
    <>
      <CallButton channel={channel} />
      <IconBtn
        icon="user"
        className="hubv-dm-prof"
        label={t('people.dm.profile')}
        tipPos="bottom"
        aria-haspopup="dialog"
        onClick={(e) => openProfile(peer.id, e.currentTarget, 'bottom-end')}
      />
    </>
  );
}
